import React, { useState } from 'react'

import { useFormContext } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import { isEmpty, isNil } from 'lodash'
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { BaseDialog } from 'components'

import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'
import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'

import { routes } from 'Routes'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'

const SendContractAnalysisButton = () => {
  const [open, setOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [loading, setLoading] = useState(false)

  const { formState } = useFormContext()
  const { dirty } = formState

  const { companySupplier } = useThirdPartyManagementStepper()
  const { company } = useAuth()

  const history = useHistory()
  const location = useLocation()
  const snackbar = useSnackbar()

  const { PDF_FILE_TYPE } = constants.validations

  const savedAttachments =
    companySupplier?.companySupplierDetail?.attachments || []

  const isPdf = (file) => !!file?.name?.toLowerCase().endsWith(PDF_FILE_TYPE)

  const pdfAttachments = (
    Array.isArray(savedAttachments) ? savedAttachments : []
  ).filter(isPdf)

  const handleOpen = () => {
    setSelectedIndex(pdfAttachments.length === 1 ? 0 : null)
    setOpen(true)
  }

  const handleClose = () => {
    if (loading) return
    setOpen(false)
    setSelectedIndex(null)
  }

  const handleContinue = async () => {
    const selected = pdfAttachments[selectedIndex]

    if (isNil(selected)) return

    try {
      setLoading(true)

      const contractFile = await helpers.file.fromUrl(
        selected.url,
        selected.name,
      )

      const supplierName =
        companySupplier?.displayName || companySupplier?.supplier?.name

      history.push(routes.technicalFaqs.contracts.ticket, {
        contractFile,
        returnTo: location.pathname,
        contractingParties: company?.name ? [company.name] : [],
        contractedParties: supplierName ? [supplierName] : [],
      })
    } catch {
      snackbar.open({
        message:
          'Não foi possível preparar o contrato para análise. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Tooltip
        title={
          isEmpty(pdfAttachments)
            ? 'Anexe um documento PDF do contrato para enviar para análise.'
            : ''
        }
      >
        <Box component="span">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleOpen}
            disabled={isEmpty(pdfAttachments)}
          >
            Enviar contrato para análise
          </Button>
        </Box>
      </Tooltip>

      <BaseDialog
        open={open}
        onClose={handleClose}
        loading={loading}
        preventCloseOnBackdropClick
        title="Enviar contrato para análise"
        dialogSize="sm"
        fullWidth
        dialogActions={
          <Box display="flex" gridGap={theme.spacing(1)}>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinue}
              disabled={isNil(selectedIndex) || loading}
            >
              {loading ? 'Preparando...' : 'Continuar'}
            </Button>
          </Box>
        }
      >
        <Box
          py={2}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
        >
          <Box>
            <Typography variant="subtitle2">
              Selecione o contrato que será enviado para análise:
            </Typography>
            <RadioGroup
              value={isNil(selectedIndex) ? '' : String(selectedIndex)}
              onChange={(event) => setSelectedIndex(Number(event.target.value))}
            >
              {pdfAttachments.map((file, index) => (
                <FormControlLabel
                  key={index}
                  value={String(index)}
                  disabled={pdfAttachments.length === 1}
                  control={<Radio />}
                  label={file?.name}
                />
              ))}
            </RadioGroup>
          </Box>
          <Typography>
            Você será redirecionado para a solicitação de análise de contrato.
            {dirty && (
              <strong>
                {' '}
                As alterações não salvas desta etapa serão perdidas.
              </strong>
            )}
          </Typography>
        </Box>
      </BaseDialog>
    </>
  )
}

export default SendContractAnalysisButton
